import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import type { ActionKey } from '../config/actions';
import { allNavItems, resolveVisibility, type NavItem } from '../config/navigation';

// allNavItems includes the pages inside sidebar hubs (Overview, Logs), which a
// plain walk over group items would miss - and then refuse.
const ITEMS: Record<string, NavItem> = Object.fromEntries(
  allNavItems().map((item) => [item.id, item]),
);

const Waiting = () => (
  <div className="flex min-h-[60vh] items-center justify-center">
    <div
      className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-indigo-600"
      role="status"
      aria-label="Loading"
    />
  </div>
);

/**
 * Route guard driven by config/navigation.ts - the same rule the sidebar uses,
 * so a page opens exactly when its menu entry is shown:
 * capability (role) AND plan feature AND role menu AND environment.
 *
 * `menu` is the nav item id, or several for detail pages reached from more
 * than one menu (a shipment opens from Tracking History, Facility Shipments
 * and Manage Orders) - any one of them is enough.
 *
 *   visible -> render the page
 *   locked  -> in the role but not the plan: Subscription & Plan (admins only
 *              ever get 'locked'; see resolveVisibility)
 *   hidden  -> Not Allowed
 */
const NavGuard: React.FC<{
  menu: string | string[];
  /** A page that is not a sidebar entry of its own (an order's page) also needs this role action. */
  action?: ActionKey;
  children: React.ReactNode;
}> = ({ menu, action, children }) => {
  const { user, isAuthenticated, can, hasFeature, hasMenuPermission, hasAction } = useAuth();
  if (!isAuthenticated || !user) return <Navigate to="/login" replace />;

  // Platform staff open tenant pages for support, as the old role lists allowed.
  if (user.role === 'super_admin') return <>{children}</>;

  // Plan not loaded yet (fresh login). Wait rather than refuse: the sidebar
  // fetches it on mount and always settles it to a list, even on failure.
  if (!user.noPlanNeed && !Array.isArray(user.activeFeatures)) return <Waiting />;

  const ctx = { can, hasFeature, hasMenuPermission, hasAction, planExempt: !!user.noPlanNeed };
  const ids = Array.isArray(menu) ? menu : [menu];
  const states = ids
    .map((id) => ITEMS[id])
    .filter((item): item is NavItem => Boolean(item))
    .map((item) => resolveVisibility(item, ctx));

  if (states.includes('visible')) {
    return !action || hasAction(action) ? <>{children}</> : <Navigate to="/not-allowed" replace />;
  }
  if (states.includes('locked')) return <Navigate to="/settings/subscription" replace />;
  return <Navigate to="/not-allowed" replace />;
};

export default NavGuard;
